import { useColors } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { useCompanyStore, companies } from '@/stores/company'
import { Building2, ChevronDown, Calendar, Filter } from 'lucide-react'
import { useState } from 'react'
import { format, addDays, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth } from 'date-fns'

interface HeaderProps {
  title: string
  subtitle?: string
  onOpenDatePicker?: () => void
  onOpenFilters?: () => void
}

export function Header({ title, subtitle, onOpenFilters }: HeaderProps) {
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  const { selectedCompany, setSelectedCompany } = useCompanyStore()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const getDaysInMonth = (date: Date) => {
    const monthStart = startOfMonth(date)
    const monthEnd = endOfMonth(date)
    const calendarStart = startOfWeek(monthStart)
    const calendarEnd = endOfWeek(monthEnd)
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }

  const days = getDaysInMonth(currentMonth)
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setShowDatePicker(false)
  }

  const isToday = (date: Date) => {
    return format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
  }

  const isSelected = (date: Date) => {
    return format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
  }

  const handleQuickSelect = (daysAgo: number) => {
    const date = subDays(new Date(), daysAgo)
    setSelectedDate(date)
    setShowDatePicker(false)
  }

  // Group days into weeks
  const weeks = days.reduce((acc: Date[][], day: Date, i: number) => {
    if (i % 7 === 0) acc.push([])
    acc[acc.length - 1].push(day)
    return acc
  }, [])

  return (
    <div 
      className="sticky top-0 z-40 border-b px-6 py-3"
      style={{ 
        backgroundColor: Colors.background.paper,
        borderBottomColor: Colors.border.main,
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-inter-semibold" style={{ color: Colors.text.primary }}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm font-inter-regular mt-1" style={{ color: Colors.text.secondary }}>
              {subtitle}
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Company Selector */}
          <div className="relative">
            <button
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-inter-medium"
              style={{ backgroundColor: Colors.neutral[200], color: Colors.neutral[800] }}
            >
              <Building2 size={16} />
              <span>{selectedCompany.name}</span>
              <ChevronDown size={14} />
            </button>
            
            {showCompanyDropdown && (
              <div 
                className="absolute top-full right-0 mt-1 w-48 rounded-md border shadow-lg z-50"
                style={{ 
                  backgroundColor: Colors.background.paper,
                  borderColor: Colors.border.main,
                }}
              >
                {companies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => {
                      setSelectedCompany(company)
                      setShowCompanyDropdown(false)
                    }}
                    className="w-full text-left px-3 py-2 text-sm font-inter-regular hover:bg-opacity-10 hover:bg-gray-500"
                    style={{ 
                      color: selectedCompany.id === company.id ? Colors.primary.main : Colors.text.primary 
                    }}
                  >
                    {company.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Picker */}
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-inter-medium"
              style={{ backgroundColor: Colors.neutral[200], color: Colors.neutral[800] }}
            >
              <Calendar size={16} />
              <span>{format(selectedDate, 'MMM d, yyyy')}</span>
              <ChevronDown size={14} />
            </button>

            {showDatePicker && (
              <div 
                className="absolute top-full right-0 mt-1 w-80 rounded-md border shadow-lg z-50 p-4"
                style={{ 
                  backgroundColor: Colors.background.paper,
                  borderColor: Colors.border.main,
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-inter-semibold" style={{ color: Colors.text.primary }}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentMonth(subDays(currentMonth, 30))}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-inter-medium"
                      style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
                    >
                      {'<'}
                    </button>
                    <button
                      onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-inter-medium"
                      style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
                    >
                      {'>'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                  {weekDays.map(day => (
                    <div 
                      key={day} 
                      className="text-center text-sm font-inter-medium py-2"
                      style={{ color: Colors.text.secondary }}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                <div className="space-y-1">
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 gap-1">
                      {week.map(date => (
                        <button
                          key={date.toString()}
                          onClick={() => handleDateSelect(date)}
                          className={`
                            w-8 h-8 rounded-md text-sm font-inter-regular flex items-center justify-center
                            ${!isSameMonth(date, currentMonth) ? 'opacity-50' : ''}
                            ${isSelected(date) ? 'text-white' : ''}
                            ${isToday(date) && !isSelected(date) ? 'ring-1' : ''}
                          `}
                          style={{ 
                            backgroundColor: isSelected(date) 
                              ? Colors.primary.main 
                              : isToday(date) && !isSelected(date)
                                ? Colors.neutral[200]
                                : 'transparent',
                            color: isSelected(date) 
                              ? '#FFFFFF' 
                              : !isSameMonth(date, currentMonth) 
                                ? Colors.text.secondary 
                                : Colors.text.primary,
                            // ringColor is not a valid CSS property; use Tailwind or boxShadow for ring effect if needed
                          }}
                        >
                          {format(date, 'd')}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>

                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => handleQuickSelect(0)}
                    className="flex-1 px-3 py-2 rounded-md text-sm font-inter-medium"
                    style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => handleQuickSelect(1)}
                    className="flex-1 px-3 py-2 rounded-md text-sm font-inter-medium"
                    style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => handleQuickSelect(7)}
                    className="flex-1 px-3 py-2 rounded-md text-sm font-inter-medium"
                    style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
                  >
                    Last Week
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          {onOpenFilters && (
            <button
              onClick={onOpenFilters}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-inter-medium"
              style={{ backgroundColor: Colors.neutral[200], color: Colors.neutral[800] }}
            >
              <Filter size={16} />
              <span>Filters</span>
              <ChevronDown size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Header